import React, { useCallback } from 'react';

import { Box, Button, Divider, Flex, Text } from '@opendoor/bricks/core';

import { useMobileDevice } from 'shared/helpers/hooks/useMobileDevice';
import { useTrackTimeToRender } from 'shared/helpers/hooks/useTrackPerformance';

import { CostBreakdown } from 'buying/components/buyDirectCheckout/CostBreakdown';
import { CustomerCareContactCard } from 'buying/components/buyDirectCheckout/CustomerCareContactCard';
import {
  BulletPoint,
  StyledUnorderedList,
} from 'buying/components/buyDirectCheckout/StyledUnorderedList';
import { ValueProps } from 'buying/components/buyDirectCheckout/ValueProps';

const bulletPoints: Array<BulletPoint> = [
  {
    headline: 'Provide a few details',
    content:
      "Let us know your contact info, if you're working with an agent, and if you need to sell your current place.",
  },
  {
    headline: 'Share how you plan to buy and your pre-approval',
    content:
      'Tell us about your financing. Upload your pre-approval letter or learn how to get pre-approved in a few minutes without hurting your credit.',
  },
  {
    headline: 'Review and sign contract',
    content:
      'Look over everything to make sure it’s correct, and we’ll create a contract for you to sign.',
  },
];

interface IReviewDetailsModuleProps {
  buyDirectDiscount: number;
  listPrice?: number;
}

/**
 * Review details section module
 * storybook-check-ignore
 */
export const ReviewDetailsModule: React.FC<IReviewDetailsModuleProps> = ({
  buyDirectDiscount,
  listPrice,
}) => {
  const { isMobile } = useMobileDevice();
  return (
    <Flex flexDirection="column" alignItems={isMobile ? 'center' : undefined}>
      <StyledUnorderedList bulletPoints={bulletPoints} title="Let’s create a contract" />
      <Divider width="100%" />
      {listPrice && <CostBreakdown buyDirectDiscount={buyDirectDiscount} listPrice={listPrice} />}
      <Divider width="100%" />
      <ValueProps />
      <Divider width="100%" />
      <Flex
        flexDirection="column"
        alignItems={['flex-start', 'flex-start', 'flex-start', 'flex-start']}
        justifyContent="flex-start"
        my={4}
        width="100%"
      >
        <Text size={isMobile ? '160' : '150'} py={8} fontWeight="medium">
          Have questions or need help?
        </Text>
        <Box width={['100%', '100%', '100%', '100%']}>
          <CustomerCareContactCard />
        </Box>
      </Flex>
    </Flex>
  );
};

interface IReviewDetailsProps {
  buyDirectDiscount: number;
  listPrice?: number;
  send: (args: string | { type: string; value: string | boolean | number | undefined }) => void;
}

/**
 * State machine wrapper
 * storybook-check-ignore
 */
export const ReviewDetails: React.FC<IReviewDetailsProps> = ({
  buyDirectDiscount,
  listPrice,
  send,
}) => {
  // PeformanceTracker: track the time it takes to render the checkout review details
  useTrackTimeToRender('checkout-reviewDetails');
  const handleOnGetStarted = useCallback(() => {
    // If the user is redirect back to this screen because of an error on offer creation, be sure to clear it
    send({ type: 'setErrorMessage', value: undefined });
    window.scrollTo(0, 0);
    send('FORWARD');
  }, [send]);

  return (
    <Flex flexDirection="column">
      <ReviewDetailsModule buyDirectDiscount={buyDirectDiscount} listPrice={listPrice} />
      <Button
        alignSelf={['center', 'center', 'flex-start', 'flex-end']}
        analyticsName="buy-direct-checkout-get-started"
        aria-label="Get started"
        data-testid="buy-direct-checkout-get-started-button"
        width={['100%', '100%', '100%', '35%']}
        variant="primary"
        onClick={handleOnGetStarted}
        mt={8}
      >
        Get started
      </Button>
    </Flex>
  );
};
